import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetWebsiteProductByIdQuery,
  useUpdateWebsiteProductMutation,
} from "../../../features/api/websiteApi";
import { API_BASE_URL } from "../../../components/baseUrl";

const ViewUpdateProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: product, isLoading: isProductLoading } =
    useGetWebsiteProductByIdQuery(id);
  const [updateWebsiteProduct, { isLoading }] =
    useUpdateWebsiteProductMutation();

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [title, setTitle] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [status, setStatus] = useState("1");

  useEffect(() => {
    if (product) {
      setTitle(product.title);
      setShortDesc(product.short_desc || "");
      setLongDesc(product.long_desc || "");
      setStatus(product.status?.toString() || "1");
      setImagePreview(`${API_BASE_URL}/uploads/${product.image}` || null);
    }
  }, [product]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      const formData = new FormData();
      if (image) formData.append("image", image);
      formData.append("title", title);
      formData.append("short_desc", shortDesc);
      formData.append("long_desc", longDesc);
      formData.append("status", status);

      await updateWebsiteProduct({ id, data: formData }).unwrap();

      toast.success("Product updated successfully!");
      navigate(`/dashboard/website/product-view/${id}`);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update product");
    }
  };

  if (isProductLoading)
    return <p className="p-6 text-gray-500">Loading product...</p>;

  return (
    <div className="px-12 my-6">
      <h2 className="text-2xl font-bold mb-4">Update Product</h2>
      <form
        onSubmit={handleSubmit}
        className=" mx-auto p-6 bg-white rounded shadow-md space-y-6"
      >
        <div className="flex items-center gap-5">
          <div className="w-full">
            <label className="block mb-1 font-medium">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Product title"
              required
            />
          </div>
          <div className="w-52">
            <label className="block mb-1 font-medium">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block mb-1 font-medium">Short Description</label>
          <textarea
            value={shortDesc}
            onChange={(e) => setShortDesc(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 resize-none"
            rows={3}
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Product Details</label>
          <ReactQuill
            theme="snow"
            value={longDesc}
            onChange={setLongDesc}
            placeholder="Enter detailed description"
          />
        </div>

        <div>
          <label className="block mb-1 font-medium">Image</label>
          <input type="file" accept="image/*" onChange={handleImageChange} />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="mt-3 w-40 h-40 object-cover rounded border border-gray-300"
            />
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-32 py-2 rounded text-white font-semibold ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isLoading ? "Updating..." : "Update Product"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ViewUpdateProduct;
